/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import React from 'react';
import { Box, Typography, Divider, FormHelperText } from '@material-ui/core';
import useContentMgmt from '../../../../../hooks/useContentMgmt';
import colors from '../../../../../themes/main-colors';
import { styled } from '@material-ui/core/styles';
import RadioButtonGroup from '../../../../pge-plus-common/RadioButtonGroup';
import microcopyGroupIds from '../../../../pge-plus-common/microcopyGroupIds';
import { Question } from '../../../../pge-plus-common/Typography';
import useFormState from '../../../../../hooks/useFormState';
import { RouteComponentProps } from '@reach/router';
import { ProceedOrCancelButton } from '../../../../pge-plus-common/StepperForm';
import routes from '../../../../../routes';
import { enrollWithVehicleOrChargerValidateFunction } from '../rebateOnlyForm.rules';
import { navigate } from 'gatsby';
import { useRebateOnlyFormState } from '../../../../../providers/RebateOnlyFormStateProvider';
import useUpdateERSections from '../../../../../hooks/pge-plus/useUpdateERSections';
import {
  ApplicationErSectionType,
  ApplicationSectionStatus,
  ErEnrollmentPreference,
  SectionErRebateInfo,
} from '../../../../../__generated__/pgeplus-types';
import useWrapWithLoader from '../../../../../hooks/useWrapWithLoading';
import { rebateInfoERFormStateAdapter } from '../rebateOnlyFormState.adapter';
import useUtil from '../../../../need-more-time-to-pay/useUtil';
import { SaveAndComeBackLink } from '../../../../pge-plus-common/SaveAndComeBackLink';

const PREFIX = 'enrollChoice';
const classes = {
  submit: `${PREFIX}-submit`,
  errorMessage: `${PREFIX}-errorMessage`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled(Box)(({ theme: theme2 }) => ({
  [`& .${classes.submit}`]: {
    backgroundColor: colors.orange,
    borderColor: colors.orange,
    '&:hover': {
      backgroundColor: colors.orange,
      borderColor: colors.orange,
    },
    [theme2.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  [`& .${classes.errorMessage}`]: {
    fontFamily: 'Untitled-Sans',
    fontSize: '1rem !important',
  },
}));

const EnrollWithVehicleOrCharger: React.FC<RouteComponentProps> = () => {
  const { content, richText } = useContentMgmt(microcopyGroupIds.PGE_PLUS_EV_REBATES_FLOW_MCG);
  const { wrapWithLoader } = useWrapWithLoader();
  const { setErrorNotification } = useUtil();
  const { updateSectionRebateInfo } = useUpdateERSections();
  const {
    formState: { rebateInfo, application },
    getSectionData,
    updateFormState
  } = useRebateOnlyFormState();

  const sectionDataRebateInfo: SectionErRebateInfo = getSectionData(ApplicationErSectionType.ErRebateInfo);

  const subText1 = { fontSize: 20, marginBottom: 16 };
  const boldText = { fontWeight: 700 };

  const form = useFormState(
    { choice: rebateInfo.enrollmentPreference ?? '' },
    {
      validate: enrollWithVehicleOrChargerValidateFunction(),
      validationContext: { content },
    }
  );

  const onSubmit = wrapWithLoader(async (data: { choice: ErEnrollmentPreference }) => {
    try {
      const response = await updateSectionRebateInfo(
        rebateInfoERFormStateAdapter().fromFormState(
          { ...rebateInfo, enrollmentPreference: data.choice },
          application?.id!,
          sectionDataRebateInfo?.id,
          ApplicationSectionStatus.Completed,
        ),
      );

      const result = response?.data?.updateSectionERRebateInfo;

      if (response?.errors || result?.validation?.errors) {
        throw new Error();
      }

      if (result?.section.data) {
        const updatedValues = rebateInfoERFormStateAdapter().toFormState(
          result?.section.data,
        );
        updateFormState('rebateInfo', {
          ...updatedValues,
        });
      }

      if (result?.section.errors.length) {
        throw new Error();
      }

      if (data.choice === ErEnrollmentPreference.UseMyCharger) {
        void navigate(routes.PGE_PLUS_REBATE_ONLY_UPLOAD_SERIAL_NUMBER);
      } else if (data.choice === ErEnrollmentPreference.UseMyVehicle) {
        void navigate(routes.PGE_PLUS_REBATE_ONLY_UPLOAD_VIN);
      }
    } catch (e) {
      setErrorNotification(true);
    }
  });

  const handleSaveAndExit = wrapWithLoader(async () => {
    try {
      const response = await updateSectionRebateInfo({
        ...rebateInfoERFormStateAdapter().fromFormState(
          {
            ...rebateInfo,
            enrollmentPreference: (form.values.choice as ErEnrollmentPreference) || undefined,
          },
          application?.id!,
          sectionDataRebateInfo?.id,
        ),
        saveAndComeBack: true,
      });

      if (
        response?.errors ||
        response.data?.updateSectionERRebateInfo?.validation?.errors ||
        response.data?.updateSectionERRebateInfo?.section?.errors.length !== 0
      ) {
        throw new Error();
      }

      void navigate(routes.ACCOUNT);
    } catch {
      setErrorNotification(true);
    }
  });

  return (
    <Root>
      <form onSubmit={form.submit(onSubmit, console.log)} noValidate>
        <Box
          marginBottom={2}
          css={css`& a {text-decoration: none;}`}
        >
        <Box>
          <Typography css={{ ...subText1, ...boldText }}>
            {content.get('PGE_PLUS_EV_REBATES_FLOW_ENROLL_CHARGER_OR_VEHICLE_QUESTION_MAIN')}
          </Typography>
        </Box>
        <Box mt={3}>
          <Typography variant="body1">
            {content.get('PGE_PLUS_EV_REBATES_FLOW_ENROLL_CHARGER_OR_VEHICLE_DESCRIPTION')}
          </Typography>
        </Box>
        <Box mt={3}>
          <Typography variant="body1" component="div">
            {richText( content.get('PGE_PLUS_EV_REBATES_FLOW_ENROLL_CHARGER_OR_VEHICLE_DESCRIPTION_CHARGER'))}
          </Typography>
        </Box>
        <Box mt={3}>
          <Typography variant="body1" component="div">
            {richText( content.get('PGE_PLUS_EV_REBATES_FLOW_ENROLL_CHARGER_OR_VEHICLE_DESCRIPTION_VEHICLE'))}
          </Typography>
        </Box>
        <Box mt={3}>
          <Question
            title={content.get('PGE_PLUS_EV_REBATES_FLOW_ENROLL_CHARGER_OR_VEHICLE_QUESTION')}
          />
          <Box
            mt={3}
            css={{
              width: 'max-content',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            <RadioButtonGroup
              options={[
                { label: content.get('PGE_PLUS_EV_REBATES_FLOW_ENROLL_CHARGER_OR_VEHICLE_USE_MY_CHARGER') || '',
                  value: ErEnrollmentPreference.UseMyCharger },
                { label: content.get('PGE_PLUS_EV_REBATES_FLOW_ENROLL_CHARGER_OR_VEHICLE_USE_MY_VEHICLE') || '',
                  value: ErEnrollmentPreference.UseMyVehicle },
              ]}
              state={form.values.choice}
              setState={choice => form.setValue('choice', choice)}
            />
              {form.errors.choice && (
                <FormHelperText className={classes.errorMessage} error>
                  {form.errors.choice}
                </FormHelperText>
              )}
          </Box>
          <Divider css={{ color: colors.gray93, margin: '30px 0 0px 0' }} />
        </Box>
      
        <ProceedOrCancelButton
            back={routes.PGE_PLUS_REBATE_ONLY_UPLOAD_MODEL_NUMBER}
            disable={!form.values.choice}
        />
        </Box>
      </form>
      <Box mt={6}>
        <SaveAndComeBackLink
          content={content}
          onSaveAndExit={handleSaveAndExit}
        />
      </Box>
    </Root>
  );
};

export default EnrollWithVehicleOrCharger;
